import { Component, inject, Input, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommunicationHistory, FileData } from 'src/app/modules/Disbursment_modules/fda-edit/comm-history.component.model';
import { FileUploadService } from '../../service/disbursment/file-upload.service';
import { FileDisplayComponent } from '../file-display/file-display.component';
import { ToastrService } from 'ngx-toastr';
import { DisbursmentService } from '../../service/disbursment/disbursment.service';

@Component({
  selector: 'app-comm-history',
  templateUrl: './comm-history.component.html',
  styleUrls: ['./comm-history.component.scss']
})
export class CommHistoryComponent {
@ViewChild(MatAccordion) accordion!: MatAccordion;
  @Input() data: CommunicationHistory[] = [];

  constructor(private snackBar: ToastrService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private fileUploadService: FileUploadService,
  ) {
  }

  communicationHistory: CommunicationHistory[] = [];
  expandedItems: boolean[] = [];
  vesselExpanded: boolean[] = [];
  merakiExpanded: boolean[] = [];
  portAgentExpanded: boolean[] = [];
  clientExpanded: boolean[] = [];
  previewUrl: SafeResourceUrl | null = null;
   private loader = inject(DisbursmentService);
  fileOpenPayload: any[] = [];

  ngOnInit() {
    this.initializeData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && changes['data'].currentValue) {
      this.initializeData();
    }
  }

  private initializeData() {
    this.communicationHistory = this.data || [];
    this.expandedItems = new Array(this.communicationHistory.length).fill(false);
    this.vesselExpanded = new Array(this.communicationHistory.length).fill(false);
    this.merakiExpanded = new Array(this.communicationHistory.length).fill(false);
    this.portAgentExpanded = new Array(this.communicationHistory.length).fill(false);
    this.clientExpanded = new Array(this.communicationHistory.length).fill(false);
  }

  toggleExpand(index: number): void {
    this.expandedItems[index] = !this.expandedItems[index];
  }

  toggleVessel(index: number): void {
    this.vesselExpanded[index] = !this.vesselExpanded[index];
  }

  toggleMeraki(index: number): void {
    this.merakiExpanded[index] = !this.merakiExpanded[index];
  }

  togglePortAgent(index: number): void {
    this.portAgentExpanded[index] = !this.portAgentExpanded[index];
  }

  toggleClient(index: number): void {
    this.clientExpanded[index] = !this.clientExpanded[index];
  }

  hasValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    return true;
  }

  isExpandable(history: CommunicationHistory): boolean {
    // Check if has modified data OR files OR modified fields
    return this.hasModifiedData(history) || this.hasFiles(history) || this.hasModifiedFields(history);
  }

  hasModifiedData(history: CommunicationHistory): boolean {
    if (!history.modified_data || !history.modified_data.modified) {
      return false;
    }

    const { vessel, meraki, port_agent, client } = history.modified_data.modified;

    // Check if any section has data
    const hasVessel = vessel?.properties?.length > 0 || vessel?.summary?.length > 0;
    const hasMeraki = meraki?.services?.length > 0 || meraki?.summary?.length > 0;
    const hasPortAgent = port_agent?.services?.length > 0 || port_agent?.summary?.length > 0 ||
      port_agent?.agreed?.length > 0 || port_agent?.negotiate?.length > 0;
    const hasClient = client?.services?.length > 0 || client?.summary?.length > 0;

    return hasVessel || hasMeraki || hasPortAgent || hasClient;
  }

  hasFiles(history: CommunicationHistory): boolean {
    return !!(history.files && history.files.length > 0);
  }

  hasModifiedFields(history: CommunicationHistory): boolean {
    return !!(history.modified_field && history.modified_field.length > 0);
  }

  getVesselProperties(history: CommunicationHistory) {
    return history.modified_data?.modified?.vessel?.properties || [];
  }

  getVesselSummary(history: CommunicationHistory) {
    return history.modified_data?.modified?.vessel?.summary || [];
  }

  getMerakiServices(history: CommunicationHistory) {
    return history.modified_data?.modified?.meraki?.services || [];
  }

  getMerakiSummary(history: CommunicationHistory) {
    return history.modified_data?.modified?.meraki?.summary || [];
  }

  hasMerakiMovements(history: CommunicationHistory): boolean {
    const services = this.getMerakiServices(history);
    return services.some(service => service.movement && service.movement.length > 0);
  }

  getPortAgentServices(history: CommunicationHistory) {
    return history.modified_data?.modified?.port_agent?.services || [];
  }

  getPortAgentSummary(history: CommunicationHistory) {
    return history.modified_data?.modified?.port_agent?.summary || [];
  }

  getPortAgentAgreed(history: CommunicationHistory) {
    return history.modified_data?.modified?.port_agent?.agreed || [];
  }

  getPortAgentNegotiate(history: CommunicationHistory) {
    return history.modified_data?.modified?.port_agent?.negotiate || [];
  }

  hasPortAgentAgreedOrNegotiate(history: CommunicationHistory): boolean {
    const agreed = this.getPortAgentAgreed(history);
    const negotiate = this.getPortAgentNegotiate(history);
    return (agreed && agreed.length > 0) || (negotiate && negotiate.length > 0);
  }

  getClientServices(history: CommunicationHistory) {
    return history.modified_data?.modified?.client?.services || [];
  }

  getClientSummary(history: CommunicationHistory) {
    return history.modified_data?.modified?.client?.summary || [];
  }

  splitSummaryLines(summary: string): string[] {
    return summary.split('\n').filter(line => line.trim().length > 0);
  }

  getUploadedFiles(comm_history_id: number | undefined, file_id: number): void {
    const data = {
      disbursement_seq: this.communicationHistory.find(history => history.comm_history_id === comm_history_id)?.disbursement_seq
    }
    this.loader.showDialogLoader();
    this.fileUploadService.fileDataForCommunicationHistory(data).subscribe({
      next: (res) => {
        this.fileOpenPayload = (res || []).filter((f: any) => f.file_id === file_id);
        this.loader.hideDialogLoader();
        this.preview(comm_history_id, this.fileOpenPayload[0], 'preview');
      },
      error: (err) => {
        this.loader.hideDialogLoader();

        const errMsg = err?.error?.error || "Error fetching files from DB";
        this.snackBar.error(errMsg);
      }
    });

  }

  preview(comm_history_id: number | undefined, file: File | FileData, uploadOrPreview: string): void {
    if (this.canPreview(file, uploadOrPreview)) {
      let url: string | undefined = undefined;

      if (file instanceof File) {
        url = URL.createObjectURL(file);
        this.openPreviewDialog(url, file.name);
      } else if ('presigned_url' in file && typeof (file as any).presigned_url === 'string') {
        this.previewWithRetry(comm_history_id, file as FileData, (file as any).presigned_url);
        return;
      }

      if (!url) {
        this.snackBar.error('Cannot preview this file.');
        return;
      }
    }
  }

  private previewWithRetry(comm_history_id: number | undefined, file: FileData, presignedUrl: string): void {
    fetch(presignedUrl, { method: 'GET' })
      .then(response => {
        if (response.status === 403) {
          // URL expired, get new presigned URL
          this.refreshPresignedUrlAndPreview(comm_history_id, file);
          return;
        }
        if (response.status === 404) {
          this.snackBar.error('File not found.');
          return;
        }
        if (response.ok) {
          this.openPreviewDialog(presignedUrl, file.name);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      })
      .catch(() => {
        this.snackBar.error('Cannot preview this file.');
      });
  }

  canPreview(file: File | FileData, uploadOrPreview: string): boolean {
    if (uploadOrPreview === 'upload' && file instanceof File) {
      return file.type.startsWith('image/') || file.type === 'application/pdf';
    }
    if (uploadOrPreview === 'preview' && 'presigned_url' in file && typeof (file as FileData).presigned_url === 'string') {
      const previewFile = file as FileData;
      const presignedUrl = previewFile.presigned_url;
      const containsValidExtension = presignedUrl.includes('.pdf') ||
        presignedUrl.includes('.png') ||
        presignedUrl.includes('.jpg') ||
        presignedUrl.includes('.jpeg');

      return containsValidExtension;
    }
    return false;
  }

  private openPreviewDialog(url: string, fileName: string): void {
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    const data = {
      url: this.previewUrl,
      name: fileName,
    };
    this.dialog.open(FileDisplayComponent, {
      data: data,
      width: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      maxWidth: '100vw',
    });
  }

  private refreshPresignedUrlAndPreview(comm_history_id: number | undefined, file: FileData): void {
    const data = {
      disbursement_seq: this.communicationHistory.find(history => history.comm_history_id === comm_history_id)?.disbursement_seq,
      // pda_fda_flag: this.existingdata.isDisbursment ? "DIS" : this.existingdata?.pdaId ? 'PDA' : 'FDA',
    };

    this.fileUploadService.fileDataFromDb(data).subscribe({
      next: (res) => {
        const updatedFile = res.find((f: any) => f.file_id === file.file_id);
        if (updatedFile?.presigned_url) {
          this.openPreviewDialog(updatedFile.presigned_url, file.name);
        } else {
          this.snackBar.error('Unable to refresh preview link.');
        }
      },
      error: () => {
        this.snackBar.error('Error refreshing preview link.');
      }
    });
  }
}
